"use client";

import React from "react";
import Image from "next/image";
import SmoothImage from "@/utils/smoothImage";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { postContact } from "@/apis/queryFunctions/contact";
import { useToast } from "@/context/ToastProvider";
import { cn } from "@/utils/cn";
import { getLenis } from "@/components/SmoothScrollProvider";
import SectionTitle from "@/components/SectionTitle";

const contactSchema = z.object({
  ho_ten: z.string().trim().min(1, "Vui lòng nhập họ tên của quý khách"),
  dien_thoai: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập số điện thoại")
    .regex(
      /^[+0-9\s]{9,}$/,
      "Số điện thoại tối thiểu 9 số, chỉ nhận ký tự số, khoảng trắng và dấu +",
    ),
  email: z
    .string()
    .trim()
    .email("Email không hợp lệ")
    .optional()
    .or(z.literal("")),
  b_url: z.string().optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

interface HighlightItem {
  title: string;
  description: string;
  icon?: string | React.ReactNode;
  href: string;
}

interface HighlightsFormSectionProps {
  defaultProduct?: string;
  highlights?: HighlightItem[];
  showForm?: boolean;
  title?: string;
}

export default function HighlightsFormSection({
  defaultProduct = "Đất nền",
  highlights,
  showForm = true,
  title = "Đặc điểm nổi bật",
}: HighlightsFormSectionProps) {
  const { showToast } = useToast();

  const finalHighlights =
    highlights && highlights.length > 0
      ? highlights.map((item) => ({
          title: item.title || "",
          description: item.description || "",
          href: item.href || "",
          icon: item.icon || "",
        }))
      : [];
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      ho_ten: "",
      dien_thoai: "",
      email: "",
    },
  });

  const contactMutation = useMutation({
    mutationFn: postContact,
    onSuccess: (rs: any) => {
      if (rs) {
        showToast(
          "Cảm ơn Quý khách. Dragon Home sẽ liên hệ tư vấn trong thời gian sớm nhất.",
          "success",
        );
        reset({
          ho_ten: "",
          dien_thoai: "",
          email: "",
        });
      } else {
        showToast(
          "Gửi thông tin không thành công. Vui lòng thử lại sau.",
          "error",
        );
      }
    },
    onError: () => {
      showToast("Có lỗi xảy ra, vui lòng thử lại sau.", "error");
    },
  });

  const onSubmit = (formData: ContactFormData) => {
    if (formData.b_url) {
      // Honeypot field was filled (bot spam).
      // Return silent success to trick the bot while ignoring the submission.
      showToast(
        "Cảm ơn Quý khách. Dragon Home sẽ liên hệ tư vấn trong thời gian sớm nhất.",
        "success",
      );
      reset({
        ho_ten: "",
        dien_thoai: "",
        email: "",
      });
      return;
    }
    const payload = {
      ho_ten: formData.ho_ten.trim(),
      dien_thoai: formData.dien_thoai.trim(),
      email: formData.email?.trim() || "",
      dia_chi: "",
      noi_dung: `Đăng ký tư vấn từ thanh Highlights - Loại hình mặc định: ${defaultProduct}`,
      loai_hinh: defaultProduct,
    };
    contactMutation.mutate(payload);
  };

  return (
    <div className="relative w-full z-20 py-12 px-4 md:px-8">
      {/* Nền white gradient top 10% để nối liền hiệu ứng với phần Hero */}
      <div className="absolute top-0 left-0 right-0 h-[20%] bg-gradient-to-b from-white to-transparent pointer-events-none z-1" />

      {/* Container Khối Đặc Điểm & Form */}
      <div className="w-full max-w-5xl mx-auto shadow-2xl flex flex-col rounded-3xl overflow-hidden">
        {/* Phần Trên: Các Đặc Điểm Nổi Bật (Nền Tối Sang Trọng) */}
        <div className="bg-gradient-to-br from-primary to-primaryDark p-6 md:p-8 lg:p-10 z-20">
          {title && (
            <SectionTitle title={title} theme="gold" className="mb-10" />
          )}
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-6 md:gap-6 lg:gap-8">
            {finalHighlights.map((item, idx) => (
              <div
                key={idx}
                className="w-[calc(50%-8px)] md:w-[calc(33.333%-16px)] lg:w-[calc(20%-26px)] flex flex-col h-full group transition-transform duration-300 hover:-translate-y-1.5"
              >
                <div className="w-10 h-10 mb-4 border border-secondary rounded-sm flex items-center justify-center text-secondary group-hover:bg-secondary group-hover:text-white transition-colors duration-300">
                  {typeof item.icon === "string" && item.icon ? (
                    <SmoothImage
                      src={item.icon}
                      alt={item.title}
                      width={20}
                      height={20}
                      className="w-5 h-5 object-contain transition-all duration-300 group-hover:brightness-0 group-hover:invert"
                    />
                  ) : (
                    item.icon
                  )}
                </div>
                <h3 className="text-sm font-bold text-white mb-2 uppercase tracking-wide">
                  {item.title}
                </h3>
                <p className="text-white/70 font-light text-xs leading-relaxed flex-grow text-justify">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Phần Dưới: Form Đăng Ký Nhanh ngang (Nền Sáng) */}
        {showForm && (
          <div className="bg-white py-5 px-6 md:px-10 border-t border-gray-100">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col md:flex-row items-end gap-6 w-full max-w-5xl mx-auto"
            >
              {/* Honeypot field for bot spam prevention */}
              <div className="hidden" aria-hidden="true">
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  placeholder="Do not fill this field"
                  {...register("b_url")}
                />
              </div>

              {/* Input Họ Tên */}
              <div className="w-full md:w-1/4 relative space-y-1.5">
                <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wide">
                  Họ và tên{" "}
                  <span className="text-[9px] font-light italic text-red-500 lowercase normal-case tracking-normal">
                    (*)
                  </span>
                </label>
                <input
                  type="text"
                  placeholder="Vui lòng nhập họ và tên"
                  {...register("ho_ten")}
                  className={cn(
                    "w-full bg-white text-black px-4 py-2.5 rounded border border-gray-300 outline-none text-xs focus:ring-1 focus:ring-primary focus:border-primary",
                    errors.ho_ten && "border-red-400 focus:border-red-400",
                  )}
                />
                {errors.ho_ten && (
                  <span className="absolute left-1 -bottom-4 text-[9px] font-semibold text-red-500">
                    {errors.ho_ten.message}
                  </span>
                )}
              </div>

              {/* Input Điện Thoại */}
              <div className="w-full md:w-1/4 relative space-y-1.5">
                <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wide">
                  Số điện thoại{" "}
                  <span className="text-[9px] font-light italic text-red-500 lowercase normal-case tracking-normal">
                    (*)
                  </span>
                </label>
                <input
                  type="tel"
                  placeholder="Vui lòng nhập số điện thoại"
                  {...register("dien_thoai")}
                  className={cn(
                    "w-full bg-white text-black px-4 py-2.5 rounded border border-gray-300 outline-none text-xs focus:ring-1 focus:ring-primary focus:border-primary",
                    errors.dien_thoai && "border-red-400 focus:border-red-400",
                  )}
                />
                {errors.dien_thoai && (
                  <span className="absolute left-1 -bottom-4 text-[9px] font-semibold text-red-500">
                    {errors.dien_thoai.message}
                  </span>
                )}
              </div>

              {/* Input Email */}
              <div className="w-full md:w-1/4 relative space-y-1.5">
                <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wide">
                  Email{" "}
                  <span className="text-[9px] font-light italic lowercase normal-case tracking-normal">
                    (Không bắt buộc)
                  </span>
                </label>
                <input
                  type="email"
                  placeholder="Vui lòng nhập email"
                  {...register("email")}
                  className={cn(
                    "w-full bg-white text-black px-4 py-2.5 rounded border border-gray-300 outline-none text-xs focus:ring-1 focus:ring-primary focus:border-primary",
                    errors.email && "border-red-400 focus:border-red-400",
                  )}
                />
                {errors.email && (
                  <span className="absolute left-1 -bottom-4 text-[9px] font-semibold text-red-500">
                    {errors.email.message}
                  </span>
                )}
              </div>

              {/* Nút Submit */}
              <div className="w-full md:w-1/4">
                <button
                  type="submit"
                  disabled={contactMutation.isPending}
                  className="w-full bg-primary hover:bg-primaryDark text-white font-bold text-xs uppercase tracking-wider py-3 rounded shadow-lg transition duration-200 cursor-pointer disabled:opacity-60 disabled:pointer-events-none"
                >
                  {contactMutation.isPending ? "Đang gửi..." : "Liên hệ ngay"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
